import { marked } from "marked";
import { parseCitation } from "../../store/selectors/chatSelectors";

// Configure marked for safe, inline-friendly output
marked.setOptions({
    breaks: true,   // Convert \n to <br>
    gfm: true,      // GitHub-flavored markdown (tables, strikethrough)
    async: false,
});

type HtmlParsedAnswer = {
    answerHtml: string;
    citations: string[];
    followupQuestions: string[];
};

/**
 * Citation placeholder token — used to protect citation markup from
 * the markdown parser (which would interpret [...] as link syntax).
 * The token is unique enough to never appear in real content.
 */
const CITATION_TOKEN = "%%CITE_";

export function parseAnswerToHtml(answer: string, isStreaming: boolean, sourceUrls?: Record<string, string>): HtmlParsedAnswer {
    const citations: string[] = [];
    const followupQuestions: string[] = [];

    // 1. Extract follow-up questions (<<...>>)
    let parsedAnswer = answer.replace(/<<([^>>]+)>>/g, (_match, content: string) => {
        followupQuestions.push(content);
        return "";
    });

    // 1b. Strip any orphaned "Follow-up questions" header the LLM emitted
    // before the bracketed questions. The agent prompt explicitly tells
    // the model NOT to include introductory text, but the model sometimes
    // does anyway — the `<<...>>` markers get extracted into the dedicated
    // UI section above, leaving a useless heading like "**Follow-up
    // questions you might ask:**" stranded in the answer body. Drop it.
    // Matches markdown headers (#…) or bold (**…**) whose visible text
    // is "follow-up question(s)" with optional trailing phrases.
    parsedAnswer = parsedAnswer.replace(
        /(?:^|\n)\s*(?:#{1,6}\s*|\*\*\s*)?follow[- ]?up\s+questions?(?:\s+you\s+might\s+ask|\s+to\s+ask|\s+to\s+consider)?\s*:?\s*\*{0,2}\s*(?=\n|$)/gi,
        "",
    );

    // 1c. Explode aggregate-citation wrappers. The LLM occasionally emits
    // its citations as a single wrapped block like:
    //   [detailed citations: uuid1::title1; uuid2::title2; uuid3::title3]
    // or [sources: ...] or [references: ...] — one outer bracket
    // containing multiple UUID::title pairs separated by ; or ,. The
    // strict citation regex below only matches `[UUID::title]` with a
    // UUID immediately after the opening bracket, so these wrappers
    // used to fall through as raw text. Rewrite each such block into
    // adjacent proper `[uuid::title]` markers so the strict parser
    // picks them up like any other citation.
    //
    // Prompt-side rule (webApp.prompt.ts §4) tells the LLM not to
    // aggregate; this is the defensive recovery when it does anyway.
    parsedAnswer = parsedAnswer.replace(
        /\[\s*(?:[a-z\s-]+):\s*([^\]]+)\]/gi,
        (match, inner: string) => {
            // Bail unless the inner text actually contains at least one
            // UUID::title pair — we don't want to eat unrelated
            // bracketed prose like `[note: this section]`.
            const pairRe = /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}::[^;,\]]+)/gi;
            const pairs = inner.match(pairRe);
            if (!pairs || pairs.length === 0) return match;
            return pairs.map((p) => `[${p.trim()}]`).join("");
        },
    );

    parsedAnswer = parsedAnswer.trim();

    // 2. Truncate incomplete citations/follow-ups during streaming
    if (isStreaming) {
        let lastIndex = parsedAnswer.length;
        for (let i = parsedAnswer.length - 1; i >= 0; i--) {
            if (parsedAnswer[i] === "]" || (i < parsedAnswer.length - 1 && parsedAnswer[i] === ">" && parsedAnswer[i + 1] === ">")) {
                break;
            } else if (parsedAnswer[i] === "[" || (i < parsedAnswer.length - 1 && parsedAnswer[i] === "<" && parsedAnswer[i + 1] === "<")) {
                lastIndex = i;
                break;
            }
        }
        parsedAnswer = parsedAnswer.substring(0, lastIndex);
    }

    // 3. Extract citations [source-id::title] and replace with placeholder tokens.
    //    This prevents the markdown parser from treating them as link syntax.
    //    Uses a regex that supports nested brackets (e.g., [uuid::title: [Domain]])
    //    by matching the UUID:: pattern and then consuming everything to the last ]].
    const citationHtmlMap: Record<string, string> = {};
    parsedAnswer = parsedAnswer.replace(/\[([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}::[^\]]*(?:\[[^\]]*\])*[^\]]*)\]/gi, (_match, content: string) => {
        let citationIndex: number;
        if (citations.indexOf(content) !== -1) {
            citationIndex = citations.indexOf(content) + 1;
        } else {
            citations.push(content);
            citationIndex = citations.length;
        }

        const parsed = parseCitation(content, sourceUrls);
        const token = `${CITATION_TOKEN}${citationIndex}%%`;
        citationHtmlMap[token] = `<a class="citation-link" href="${parsed.url}" data-citation-path="${parsed.url}" data-source-id="${parsed.sourceId}" title="${parsed.displayTitle}" role="button" tabindex="0"><sup>${citationIndex}</sup></a>`;
        return token;
    });

    // 3a. Sweep stray closing brackets immediately after a citation token.
    //     The model occasionally double-closes — emits `[uuid::title]]` — and
    //     our regex matches the inner `[uuid::title]` cleanly, leaving the
    //     extra `]` as text that renders next to the superscript. Drop it.
    parsedAnswer = parsedAnswer.replace(
        new RegExp(`(${CITATION_TOKEN}\\d+%%)\\]`, "g"),
        "$1",
    );

    // 3b. Strip hallucinated citations. When the LLM invents a citation —
    //     wrong UUID length, made-up date suffix, etc. — the strict regex
    //     above doesn't match and the raw `[garbage]` falls through to the
    //     reader as visible text. Catch anything that LOOKS citation-shaped
    //     (bracketed, contains `::`, hex-ish prefix) but didn't get tokenized,
    //     and drop it silently. Better an absent citation than a leaked
    //     internal ID.
    parsedAnswer = parsedAnswer.replace(
        /\[[0-9a-f][0-9a-f-]{10,}::[^\]]*\]/gi,
        "",
    );

    // 4. Merge citation-only lines into the preceding line.
    //    The LLM puts citations on their own lines (bulleted or bare), e.g.:
    //      "   - Home visits provide support.\n   %%CITE_1%% %%CITE_2%%"
    //    or: "   - %%CITE_1%%"
    //    We append the citation tokens to the previous line so superscripts
    //    appear inline, then remove any lines that are still citation-only.
    const citationTokenPattern = new RegExp(`${CITATION_TOKEN}\\d+%%`, "g");
    const lines = parsedAnswer.split("\n");
    const merged: string[] = [];
    for (const line of lines) {
        const stripped = line.replace(/^\s*[-*]\s*/, "").trim();
        // Check if line is only citation tokens (with optional commas/spaces)
        const withoutCitations = stripped.replace(citationTokenPattern, "").replace(/[,\s]/g, "");
        if (withoutCitations === "" && stripped !== "") {
            // Line is citation-only — append tokens to previous line
            const tokens = stripped.match(citationTokenPattern);
            if (tokens && merged.length > 0) {
                // Append to the end of the previous non-empty content line
                merged[merged.length - 1] = merged[merged.length - 1].trimEnd() + " " + tokens.join(" ");
            }
            // Drop this line either way
        } else {
            merged.push(line);
        }
    }
    parsedAnswer = merged.join("\n");

    // 5. Remove trailing "Sources:" / "References:" sections that the LLM sometimes adds.
    //    After citation extraction, these become orphaned headers with no content.
    //    Match: "Sources:" (with optional markdown header prefix) through end of string,
    //    but only if the remaining content is just whitespace/empty lines/citation tokens.
    const trailingSources = new RegExp(
        `(?:^|\\n)\\s*(?:#{1,4}\\s*)?(?:Sources|References|Citations)\\s*:?\\s*(?:\\n(?:[-*\\s]|${CITATION_TOKEN}\\d+%%)*)*$`,
        "i"
    );
    parsedAnswer = parsedAnswer.replace(trailingSources, "").trim();

    // 6. Render markdown to HTML
    let html = (marked.parse(parsedAnswer) as string).trim();

    // 7. Restore citation placeholders with actual HTML
    for (const [token, citationHtml] of Object.entries(citationHtmlMap)) {
        html = html.split(token).join(citationHtml);
    }

    return {
        answerHtml: html,
        citations,
        followupQuestions
    };
}
